import React, { useMemo } from 'react';
import { Box, Text } from '@mantine/core';
import { useQuery } from '@apollo/client';
import {
    IconAlertCircle,
    IconAlertTriangle,
    IconBan,
    IconCircleCheck,
    IconCircleDot,
    IconCirclePlus,
    IconCircleX,
    IconClockHour8,
    IconDotsCircleHorizontal,
} from '@tabler/icons-react';
import type {
    PriceChangeRequestHistoryItem,
    Request,
} from '../../../codegen/graphql';
import { GET_PRICE_CHANGE_REQUEST_HISTORY } from '../../../apollo/queries/priceChangeRequest';
import {
    getPricingRequestStatusLabel,
    isPricingRequestUrgent,
} from '../../../utility/pricingRequestStatus';
import { getLastResolvedHistoryItem } from '../../../utility/requestHistoryPreview';
import { formatHistoryItemPreviewSubtitle } from '../history/PriceChangeRequestHistoryTimelineItem';
import { PriceChangeRequestHistoryTimeline } from '../history/PriceChangeRequestHistoryTimeline';
import { REQUEST_HISTORY_CELL_DETAIL_TRIGGER_CLASSNAME } from './cellDetailSurfaceClasses';
import { CellDetailTriggerChevron } from './cellDetailTriggerChevron';
import { TableCellDetailPopover } from './TableCellDetailPopover';

type Props = {
    request: Request;
};

const REQUEST_HISTORY_PREVIEW_ICON_SIZE = 26;
const REQUEST_HISTORY_PREVIEW_ICON_STROKE = 1.75;

const LABEL_ICON_CONFIG = {
    'New Request': {
        Icon: IconCircleDot,
        iconClass: 'text-primary',
    },
    'Interim Price': {
        Icon: IconClockHour8,
        iconClass: 'text-purple-600',
    },
    InterimPmApproval: {
        Icon: IconAlertTriangle,
        iconClass: 'text-amber-600',
    },
    Pending: {
        Icon: IconDotsCircleHorizontal,
        iconClass: 'text-yellow-600',
    },
    Approved: {
        Icon: IconCircleCheck,
        iconClass: 'text-emerald-600',
    },
    Declined: {
        Icon: IconCircleX,
        iconClass: 'text-red-600',
    },
    'Auto-declined': {
        Icon: IconBan,
        iconClass: 'text-orange-500',
    },
    Urgent: {
        Icon: IconAlertCircle,
        iconClass: 'text-red-600',
    },
    default: {
        Icon: IconCirclePlus,
        iconClass: 'text-gray-600',
    },
} as const;

type LabelIconKey = keyof typeof LABEL_ICON_CONFIG;

function RequestHistoryPreviewIcon({ label }: { label: LabelIconKey }) {
    const { Icon, iconClass } = LABEL_ICON_CONFIG[label];
    return (
        <Icon
            size={REQUEST_HISTORY_PREVIEW_ICON_SIZE}
            stroke={REQUEST_HISTORY_PREVIEW_ICON_STROKE}
            className={`shrink-0 ${iconClass}`}
        />
    );
}

function previewTitleFromTableStatus(label: string): string {
    if (label === 'New Request') {
        return 'New request';
    }
    if (label === 'Interim Price') {
        return 'Interim price';
    }
    if (label === 'Urgent') {
        return 'Urgent';
    }
    if (label === 'Auto-declined') {
        return 'Auto-declined';
    }
    return `Request ${label}`;
}

function iconKeyFromResolvedHistory(
    item: PriceChangeRequestHistoryItem,
): LabelIconKey {
    const { status, title } = item;
    if (status.includes('Declined')) {
        return 'Declined';
    }

    const titleLower = title.toLowerCase();
    const isProcurementInterimApproval =
        status === 'ApprovedWithTemporaryPrice' ||
        titleLower.includes('approved with interim');

    if (isProcurementInterimApproval) {
        return 'InterimPmApproval';
    }

    if (titleLower.includes('interim')) {
        return 'Interim Price';
    }

    if (status.includes('Approved')) {
        return 'Approved';
    }
    return 'default';
}

export function RequestHistoryTableCell({ request }: Props) {
    const fallbackStatusLabel = getPricingRequestStatusLabel(request);
    const urgent = isPricingRequestUrgent(request);

    const { data, loading, error } = useQuery(
        GET_PRICE_CHANGE_REQUEST_HISTORY,
        {
            variables: { id: request.id },
            fetchPolicy: 'cache-first',
        },
    );

    const items = data?.getPriceChangeRequestHistory?.history ?? [];

    const lastResolved = useMemo(
        () => getLastResolvedHistoryItem(items),
        [items],
    );

    const primaryTitle = lastResolved?.title
        ? lastResolved.title
        : previewTitleFromTableStatus(fallbackStatusLabel);

    const subtitle = useMemo(
        () =>
            lastResolved
                ? formatHistoryItemPreviewSubtitle(lastResolved)
                : 'No activity yet',
        [lastResolved],
    );

    const historyDerivedIconKey = useMemo(
        () => (lastResolved ? iconKeyFromResolvedHistory(lastResolved) : null),
        [lastResolved],
    );

    const iconKey: LabelIconKey = useMemo(() => {
        const resolvedBlocksUrgent =
            historyDerivedIconKey === 'InterimPmApproval' ||
            historyDerivedIconKey === 'Approved';

        if (resolvedBlocksUrgent && historyDerivedIconKey) {
            return historyDerivedIconKey;
        }
        if (historyDerivedIconKey === 'Declined') {
            return request.isAutoDeclined ? 'Auto-declined' : 'Declined';
        }
        if (urgent) {
            return 'Urgent';
        }
        if (historyDerivedIconKey) {
            return historyDerivedIconKey;
        }
        return (
            fallbackStatusLabel in LABEL_ICON_CONFIG
                ? fallbackStatusLabel
                : 'default'
        ) as LabelIconKey;
    }, [
        historyDerivedIconKey,
        urgent,
        fallbackStatusLabel,
        request.isAutoDeclined,
    ]);

    const timelineColor =
        request.latestStatus !== 'Declined' ? 'primary' : 'red';

    const trigger = (
        <div className="flex h-full w-full min-h-0 items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <RequestHistoryPreviewIcon label={iconKey} />
                <Box className="min-w-0 flex-1 overflow-hidden text-left">
                    <Text
                        size="sm"
                        fw={600}
                        className="leading-tight text-gray-900 line-clamp-1"
                    >
                        {primaryTitle}
                    </Text>
                    <Text
                        size="xs"
                        className="leading-tight text-gray-500 line-clamp-1"
                    >
                        {subtitle}
                    </Text>
                </Box>
            </div>
            <CellDetailTriggerChevron />
        </div>
    );

    return (
        <div className="flex w-full justify-start">
            <TableCellDetailPopover
                title="Request History"
                trigger={trigger}
                triggerClassName={REQUEST_HISTORY_CELL_DETAIL_TRIGGER_CLASSNAME}
            >
                <PriceChangeRequestHistoryTimeline
                    items={items}
                    loading={loading}
                    error={error ?? undefined}
                    timelineColor={timelineColor}
                />
            </TableCellDetailPopover>
        </div>
    );
}
